var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/index.tsx
import * as React from "react";
import { useMemo } from "react";
import qrGenerator from "qrcode-generator";
var VIEWPORT_SIZE = 1e3;
var RE_CODE_LENGTH_OVERFLOW = /code length overflow/i;
qrGenerator.stringToBytes = qrGenerator.stringToBytesFuncs["UTF-8"];
var createQrCode = (text, level, minVer) => {
  minVer = Math.max(1, minVer);
  for (let version = minVer; version <= 40; version += 1) {
    try {
      const qr = qrGenerator(version, level);
      qr.addData(text);
      qr.make();
      const moduleCount = qr.getModuleCount();
      const isDark = (row, col) => {
        return row >= 0 && row < moduleCount && col >= 0 && col < moduleCount && qr.isDark(row, col);
      };
      return { text, level, version, moduleCount, isDark };
    } catch (err) {
      if (!(version < 40 && RE_CODE_LENGTH_OVERFLOW.test(String(err)))) {
        throw err;
      }
    }
  }
  return null;
};
var round = Math.round;
var drawM = (x, y) => {
  return `M ${round(x)} ${round(y)}`;
};
var drawL = (x, y) => {
  return `L ${round(x)} ${round(y)}`;
};
var drawA = (x, y, rad) => {
  return `A ${round(rad)} ${round(rad)} 0 0 1 ${round(x)} ${round(y)}`;
};
var drawDark = (l, t, r, b, rad, nw, ne, se, sw) => {
  const path = [];
  if (nw) {
    path.push(drawM(l + rad, t));
  } else {
    path.push(drawM(l, t));
  }
  if (ne) {
    path.push(drawL(r - rad, t), drawA(r, t + rad, rad));
  } else {
    path.push(drawL(r, t));
  }
  if (se) {
    path.push(drawL(r, b - rad), drawA(r - rad, b, rad));
  } else {
    path.push(drawL(r, b));
  }
  if (sw) {
    path.push(drawL(l + rad, b), drawA(l, b - rad, rad));
  } else {
    path.push(drawL(l, b));
  }
  if (nw) {
    path.push(drawL(l, t + rad), drawA(l + rad, t, rad));
  } else {
    path.push(drawL(l, t));
  }
  return path.join(" ");
};
var drawLight = (l, t, r, b, rad, nw, ne, se, sw) => {
  const path = [];
  if (nw) {
    path.push(drawM(l + rad, t), drawL(l, t), drawL(l, t + rad), drawA(l + rad, t, rad));
  }
  if (ne) {
    path.push(drawM(r, t + rad), drawL(r, t), drawL(r - rad, t), drawA(r, t + rad, rad));
  }
  if (se) {
    path.push(drawM(r - rad, b), drawL(r, b), drawL(r, b - rad), drawA(r - rad, b, rad));
  }
  if (sw) {
    path.push(drawM(l, b - rad), drawL(l, b), drawL(l + rad, b), drawA(l, b - rad, rad));
  }
  return path.join(" ");
};
var drawMod = (qr, rounding, width, row, col) => {
  const left = col * width;
  const top = row * width;
  const right = left + width;
  const bottom = top + width;
  const radius = rounding * 5e-3 * width;
  const isDark = qr.isDark;
  const rowN = row - 1;
  const rowS = row + 1;
  const colW = col - 1;
  const colE = col + 1;
  const darkCenter = isDark(row, col);
  const darkNw = isDark(rowN, colW);
  const darkN = isDark(rowN, col);
  const darkNe = isDark(rowN, colE);
  const darkE = isDark(row, colE);
  const darkSe = isDark(rowS, colE);
  const darkS = isDark(rowS, col);
  const darkSw = isDark(rowS, colW);
  const darkW = isDark(row, colW);
  if (darkCenter) {
    return drawDark(left, top, right, bottom, radius, !darkN && !darkW, !darkN && !darkE, !darkS && !darkE, !darkS && !darkW);
  } else {
    return drawLight(left, top, right, bottom, radius, darkN && darkW && darkNw, darkN && darkE && darkNe, darkS && darkE && darkSe, darkS && darkW && darkSw);
  }
};
var createPath = (qr, rounding) => {
  if (!qr) {
    return "";
  }
  const modCount = qr.moduleCount;
  const modSize = VIEWPORT_SIZE / modCount;
  const path = [];
  for (let row = 0; row < modCount; row += 1) {
    for (let col = 0; col < modCount; col += 1) {
      path.push(drawMod(qr, rounding, modSize, row, col));
    }
  }
  return path.join(" ");
};
var QR = (_a) => {
  var _b = _a, {
    color = "#000",
    backgroundColor,
    cutout = false,
    cutoutElement,
    errorCorrectionLevel = "Q",
    rounding = 0,
    children
  } = _b, props = __objRest(_b, [
    "color",
    "backgroundColor",
    "cutout",
    "cutoutElement",
    "errorCorrectionLevel",
    "rounding",
    "children"
  ]);
  var _a2, _b2;
  const qr = useMemo(() => createQrCode(children, errorCorrectionLevel, 1), [children, errorCorrectionLevel]);
  if (!qr) {
    return null;
  }
  const moduleCount = (_a2 = qr == null ? void 0 : qr.moduleCount) != null ? _a2 : 1;
  const moduleSize = VIEWPORT_SIZE / ((_b2 = qr == null ? void 0 : qr.moduleCount) != null ? _b2 : 1);
  const cutoutSize = moduleSize * (Math.floor(moduleCount * 0.18) * 2 - 1) + 2;
  const cutoutPosition = (VIEWPORT_SIZE - cutoutSize) / 2;
  return /* @__PURE__ */ React.createElement("svg", __spreadValues({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: `0 0 ${VIEWPORT_SIZE} ${VIEWPORT_SIZE}`
  }, props), cutout ? /* @__PURE__ */ React.createElement("mask", {
    id: "react-qr-rounded__mask"
  }, /* @__PURE__ */ React.createElement("rect", {
    x: "0",
    y: "0",
    width: VIEWPORT_SIZE,
    height: VIEWPORT_SIZE,
    fill: "#fff"
  }), /* @__PURE__ */ React.createElement("rect", {
    x: cutoutPosition,
    y: cutoutPosition,
    width: cutoutSize,
    height: cutoutSize,
    rx: rounding,
    ry: rounding,
    fill: "#000"
  })) : null, backgroundColor ? /* @__PURE__ */ React.createElement("rect", {
    x: "0",
    y: "0",
    width: VIEWPORT_SIZE,
    height: VIEWPORT_SIZE,
    fill: backgroundColor
  }) : null, /* @__PURE__ */ React.createElement("path", {
    fill: color,
    d: createPath(qr, rounding),
    mask: cutout ? "url(#react-qr-rounded__mask)" : void 0
  }), cutoutElement ? /* @__PURE__ */ React.createElement("foreignObject", {
    x: cutoutPosition,
    y: cutoutPosition,
    width: cutoutSize,
    height: cutoutSize
  }, /* @__PURE__ */ React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
      overflow: "hidden",
      borderRadius: rounding
    }
  }, cutoutElement)) : null);
};
export {
  QR
};
